import type { ICollectionMetafields } from "../types/api/collection";
import type { IProductMetafields } from "../types/api/product";
import type { Collection, Customer, Metaobject, Product } from "../types/generated/storefront.types";

// 멤버십 유효성 확인
export const checkMembershipAccessible = (
  data: (Collection & ICollectionMetafields) | (Product & IProductMetafields) | Metaobject | undefined,
  customer: Customer,
) => {
  let accessibleMemberships: string[] = [];

  if (data?.__typename === "Metaobject") {
    accessibleMemberships = JSON.parse(data.fields.find((item) => item.key === "accessible_memberships")?.value || "[]");
  } else if (data?.__typename === "Collection" || data?.__typename === "Product") {
    accessibleMemberships = JSON.parse(data.accessible_memberships?.value || "[]");
  }

  if (accessibleMemberships.length > 0) {
    return customer.tags.some((tag) => accessibleMemberships.includes(tag));
  }

  return true;
};

export const MEMBERSHIP_INFO = {
  tune: {
    ko: [
      { title: "WHITE", lower: 199999, higher: 0, discount: 0, coupon: 0, fileName: "" },
      { title: "GREEN", lower: 499999, higher: 200000, discount: 1, coupon: 10, fileName: "" },
      { title: "SILVER", lower: 999999, higher: 500000, discount: 3, coupon: 10, fileName: "" },
      { title: "GOLD", lower: 4999999, higher: 1000000, discount: 5, coupon: 10, fileName: "" },
      { title: "PLATINUM", lower: 9999999, higher: 5000000, discount: 7, coupon: 10, fileName: "" },
      { title: "BLACK", lower: 10000000, higher: 10000000, discount: 10, coupon: 10, fileName: "" },
    ],
    en: [
      { title: "WHITE", lower: 199999, higher: 0, discount: 0, coupon: 0, fileName: "" },
      { title: "GREEN", lower: 499999, higher: 200000, discount: 1, coupon: 10, fileName: "" },
      { title: "SILVER", lower: 999999, higher: 500000, discount: 3, coupon: 10, fileName: "" },
      { title: "GOLD", lower: 4999999, higher: 1000000, discount: 5, coupon: 10, fileName: "" },
      { title: "PLATINUM", lower: 9999999, higher: 5000000, discount: 7, coupon: 10, fileName: "" },
      { title: "BLACK", lower: 10000000, higher: 10000000, discount: 10, coupon: 10, fileName: "" },
    ],
  },
  thisisneverthat: {
    ko: [
      { title: "MEMBER", lower: 300000, higher: 0, discount: 1, coupon: 0, fileName: "" },
      { title: "SILVER", lower: 2000000, higher: 300000, discount: 3, coupon: 5, fileName: "" },
      { title: "GOLD", lower: 5000000, higher: 2000000, discount: 5, coupon: 7, fileName: "" },
      { title: "PLATINUM", lower: 10000000, higher: 5000000, discount: 7, coupon: 10, fileName: "" },
      { title: "VIP", lower: 10000000, higher: 10000000, discount: 10, coupon: 15, fileName: "" },
    ],
    en: [
      { title: "MEMBER", lower: 300, higher: 0, discount: 1, coupon: 0, fileName: "" },
      { title: "SILVER", lower: 2000, higher: 300, discount: 3, coupon: 5, fileName: "" },
      { title: "GOLD", lower: 5000, higher: 2000, discount: 5, coupon: 7, fileName: "" },
      { title: "PLATINUM", lower: 10000, higher: 5000, discount: 7, coupon: 10, fileName: "" },
      { title: "VIP", lower: 10000, higher: 10000, discount: 10, coupon: 15, fileName: "" },
    ],
  },
  khakis: {
    ko: [
      { title: "REGULAR", lower: 500000, higher: 0, discount: 0, coupon: 0, fileName: "khakis-membership-regular" },
      { title: "GREEN", lower: 2000000, higher: 500000, discount: 3, coupon: 0, fileName: "khakis-membership-green" },
      {
        title: "SILVER",
        lower: 5000000,
        higher: 2000000,
        discount: 5,
        coupon: 3,
        fileName: "khakis-membership-silver",
      },
      { title: "GOLD", lower: 10000000, higher: 5000000, discount: 7, coupon: 5, fileName: "khakis-membership-gold" },
      {
        title: "BLACK",
        lower: 20000000,
        higher: 10000000,
        discount: 10,
        coupon: 7,
        fileName: "khakis-membership-black",
      },
      {
        title: "PLATINUM",
        lower: 0,
        higher: 20000000,
        discount: 15,
        coupon: 10,
        fileName: "khakis-membership-platinum",
      },
    ],
    en: [
      { title: "REGULAR", lower: 500, higher: 0, discount: 0, coupon: 0, fileName: "khakis-membership-regular" },
      { title: "GREEN", lower: 2000, higher: 500, discount: 3, coupon: 0, fileName: "khakis-membership-green" },
      { title: "SILVER", lower: 5000, higher: 2000, discount: 5, coupon: 3, fileName: "khakis-membership-silver" },
      { title: "GOLD", lower: 10000, higher: 5000, discount: 7, coupon: 5, fileName: "khakis-membership-gold" },
      { title: "BLACK", lower: 20000, higher: 10000, discount: 10, coupon: 7, fileName: "khakis-membership-black" },
      { title: "PLATINUM", lower: 0, higher: 20000, discount: 15, coupon: 10, fileName: "khakis-membership-platinum" },
    ],
  },
  sunlove: { ko: [], en: [] },
  // tenui 는 멤버십 등급 제도가 없다 (토스 임직원 할인만 별도로 운영)
  tenui: { ko: [], en: [] },
  yeseyesee: {
    ko: [
      { title: "WELCOME", lower: 300000, higher: 0, discount: 1, coupon: 0, fileName: "" },
      { title: "FRIENDS", lower: 700000, higher: 300000, discount: 3, coupon: 5, fileName: "" },
      { title: "FAMILY", lower: 2000000, higher: 700000, discount: 5, coupon: 7, fileName: "" },
      { title: "PLATINUM", lower: 5000000, higher: 2000000, discount: 7, coupon: 10, fileName: "" },
      { title: "VIP", lower: 0, higher: 5000000, discount: 10, coupon: 15, fileName: "" },
    ],
    en: [
      { title: "WELCOME", lower: 300, higher: 0, discount: 1, coupon: 0, fileName: "" },
      { title: "FRIENDS", lower: 700, higher: 300, discount: 3, coupon: 5, fileName: "" },
      { title: "FAMILY", lower: 2000, higher: 700, discount: 5, coupon: 7, fileName: "" },
      { title: "PLATINUM", lower: 5000, higher: 2000, discount: 7, coupon: 10, fileName: "" },
      { title: "VIP", lower: 0, higher: 5000, discount: 10, coupon: 15, fileName: "" },
    ],
  },
  wellbeingexpress: {
    ko: [
      { title: "LAYER0", lower: 0, higher: 500000, discount: 0, coupon: 0, fileName: "" },
      {
        title: "LAYER1",
        lower: 500000,
        higher: 800000,
        discount: 0,
        coupon: 5,
        fileName: "LAYER 1 달성 시 웰컴 키트 증정",
      },
      { title: "LAYER2", lower: 800000, higher: 1100000, discount: 0, coupon: 7, fileName: "" },
      { title: "LAYER3", lower: 1100000, higher: 1400000, discount: 0, coupon: 10, fileName: "" },
      { title: "LAYER4", lower: 1400000, higher: 1700000, discount: 0, coupon: 15, fileName: "" },
      { title: "LAYER5", lower: 1700000, higher: 2000000, discount: 0, coupon: 15, fileName: "SPECIAL GIFT" },
    ],
    en: [
      { title: "LAYER0", lower: 0, higher: 500000, discount: 0, coupon: 0, fileName: "" },
      {
        title: "LAYER1",
        lower: 500000,
        higher: 800000,
        discount: 0,
        coupon: 5,
        fileName: "LAYER 1 달성 시 웰컴 키트 증정",
      },
      { title: "LAYER2", lower: 800000, higher: 1100000, discount: 0, coupon: 7, fileName: "" },
      { title: "LAYER3", lower: 1100000, higher: 1400000, discount: 0, coupon: 10, fileName: "" },
      { title: "LAYER4", lower: 1400000, higher: 1700000, discount: 0, coupon: 15, fileName: "" },
      { title: "LAYER5", lower: 1700000, higher: 2000000, discount: 0, coupon: 15, fileName: "SPECIAL GIFT" },
    ],
  },
};
